import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { 
  Person as PersonIcon, 
  Email as EmailIcon, 
  AccessTime as AccessTimeIcon,
  SelfImprovement as MeditationIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Helper function to format dates in a user-friendly way
const formatUserFriendlyDate = (dateString: string | undefined, isRecent: boolean = false): string => {
  if (!dateString) return 'Unknown';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  // For recent activities (like last sign in), show relative time
  if (isRecent) {
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
    if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
    if (diffInYears >= 1) return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
  }

  // For older dates (like account creation), show formatted date
  const today = new Date();
  const isThisYear = date.getFullYear() === today.getFullYear();
  
  if (isThisYear) {
    return date.toLocaleDateString(undefined, { 
      month: 'long', 
      day: 'numeric' 
    }); // e.g., "January 15"
  } else {
    return date.toLocaleDateString(undefined, { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    }); // e.g., "January 15, 2023"
  }
};

// Helper function to get a more descriptive label for creation time
const getCreationTimeLabel = (dateString: string | undefined): string => {
  if (!dateString) return 'Member Since';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays < 7) return 'New Member';
  if (diffInDays < 30) return 'Recent Member';
  if (diffInDays < 365) return 'Member Since';
  return 'Member Since';
};

const UserProfile: React.FC = () => {
  const { currentUser } = useAuth();

  if (!currentUser) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar
          src={currentUser.photoURL || undefined}
          alt={currentUser.displayName || 'User'}
          sx={{ width: 64, height: 64, mr: 2 }}
        />
        <Box>
          <Typography variant="h6" component="h2">
            {currentUser.displayName || 'Meditation User'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentUser.email}
          </Typography>
          <Chip 
            label="Authenticated" 
            color="success" 
            size="small" 
            sx={{ mt: 1 }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Account Information
      </Typography>
      
      <List dense>
        <ListItem>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText
            primary="Display Name"
            secondary={currentUser.displayName || 'Not provided'}
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <EmailIcon />
          </ListItemIcon>
          <ListItemText
            primary="Email"
            secondary={currentUser.email}
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <AccessTimeIcon />
          </ListItemIcon>
          <ListItemText
            primary={getCreationTimeLabel(currentUser.metadata?.creationTime)}
            secondary={formatUserFriendlyDate(currentUser.metadata?.creationTime, false)}
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <MeditationIcon />
          </ListItemIcon>
          <ListItemText
            primary="Last Sign In"
            secondary={formatUserFriendlyDate(currentUser.metadata?.lastSignInTime, true)}
          />
        </ListItem>
      </List>

    </Paper>
  );
};

export default UserProfile;
